// src/api/authApi.js
import axios from 'axios';

// ✅ Use the correct base URL for your backend
const API_BASE_URL = 'https://admin.thefoodies.in/api';

class ApiService {
  constructor() {
    // ✅ PRIVATE API - For authenticated requests (with token)
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // ✅ PUBLIC API - For unauthenticated requests (NO token)
    this.publicApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // ===== PRIVATE API INTERCEPTORS =====
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token && token !== 'undefined' && token !== 'null') {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('🔑 Token attached to request:', config.url);
        } else {
          console.warn('⚠️ No valid token for:', config.url);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('🔐 Authentication failed. Please login again.');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isNewUser');
          localStorage.removeItem('profileCompleted');
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Network error occurred';
        return Promise.reject(errorMessage);
      }
    );

    // ===== PUBLIC API INTERCEPTORS =====
    this.publicApi.interceptors.request.use(
      (config) => {
        console.log('🌐 Public API Request:', {
          url: config.url,
          method: config.method,
          data: config.data
        });
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.publicApi.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('❌ Public API Error:', {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data
        });
        
        const errorMessage = error.response?.data?.message || 
                           error.response?.data?.error || 
                           error.message || 
                           'Network error occurred';
        return Promise.reject(errorMessage);
      }
    );
  }

  // ========== AUTH API (PUBLIC - No Token Required) ==========
  
  sendOTP = async (phone) => {
    try {
      const phoneStr = String(phone).trim();
      console.log('📞 Sending OTP for:', phoneStr);
      
      if (!phoneStr || phoneStr.length !== 10) {
        throw new Error('Please enter a valid 10-digit phone number');
      }
      
      const response = await this.publicApi.post('/send-otp', { phone: phoneStr });
      console.log('✅ OTP sent:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      throw error;
    }
  };

  verifyOTP = async (phone, otp) => {
    try {
      const phoneStr = String(phone).trim();
      const otpStr = String(otp).trim();
      
      console.log('🔐 Verifying OTP for:', phoneStr);
      
      if (!phoneStr || phoneStr.length !== 10) {
        throw new Error('Invalid phone number');
      }
      if (!otpStr || otpStr.length !== 6) {
        throw new Error('Please enter a valid 6-digit OTP');
      }
      
      const response = await this.publicApi.post('/verify-otp', { 
        phone: phoneStr,
        otp: otpStr
      });
      
      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.user_id,
          phone: phoneStr,
          name: response.data.name || 'User'
        }));
        localStorage.setItem('isNewUser', response.data.is_new_user ? 'true' : 'false');
        console.log('✅ Token saved successfully');
      }
      
      console.log('✅ OTP verified:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Verify OTP error:', error);
      throw error;
    }
  };

  // ========== MEAL OFF API (PRIVATE - Token Required) ==========
  
  getMealOffs = async (subscriptionId) => {
    try {
      console.log('🔄 POST - Fetching meal offs for subscription:', subscriptionId);
      
      const response = await this.api.post(`/tiffin/monthly/${subscriptionId}/meal-off`, {});
      
      console.log('✅ POST - Meal offs fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Meal offs error:', error);
      
      if (error.response?.status === 404) {
        console.warn('⚠️ No meal offs found for subscription:', subscriptionId);
        return { status: true, data: [] };
      }
      throw error;
    }
  };

  markMealOff = async (subscriptionId, date, mealTime, mealType) => {
    try {
      if (!subscriptionId) {
        throw new Error('Subscription ID is required');
      }
      if (!date) {
        throw new Error('Date is required');
      }
      if (!mealTime) {
        throw new Error('Meal time is required');
      }
      
      const formattedDate = date;
      const payload = {
        date: formattedDate,
        meal_time: mealTime,
        
      };
      
      console.log('🔄 POST - Marking meal off:', { 
        subscriptionId, 
        ...payload
      });
      
      const response = await this.api.post(`/tiffin/monthly/${subscriptionId}/meal-off`, payload);
      
      console.log('✅ POST - Meal marked off:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Mark meal off error:', error);
      console.error('❌ Error response:', error.response?.data);
      throw error;
    }
  };

  removeMealOff = async (subscriptionId, date, mealTime, mealType) => {
    try {
      if (!subscriptionId) {
        throw new Error('Subscription ID is required');
      }
      if (!date) {
        throw new Error('Date is required');
      }
      if (!mealTime) {
        throw new Error('Meal time is required');
      }
      
      const payload = {
        date: date,
        meal_time: mealTime,
        meal_type: mealType || 'Veg'
      };
      
      console.log('🔄 DELETE - Removing meal off:', { 
        subscriptionId, 
        ...payload
      });
      
      const response = await this.api.delete(`/tiffin/monthly/${subscriptionId}/meal-off`, {
        data: payload
      });
      
      console.log('✅ DELETE - Meal off removed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ DELETE - Remove meal off error:', error);
      throw error;
    }
  };

  // ========== WEEKLY MENU API (PRIVATE - Token Required) ==========
  
  getWeeklyMenu = async () => {
    try {
      console.log('🔄 GET - Fetching weekly menu...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first to view menu');
      }
      
      const response = await this.api.get('/tiffin/weekly-menu');
      console.log('✅ GET - Weekly menu raw response:', response.data);
      
      if (response.data?.status === true && response.data?.data) {
        const groupedData = response.data.data;
        const flatArray = [];
        
        Object.keys(groupedData).forEach(day => {
          if (Array.isArray(groupedData[day])) {
            groupedData[day].forEach(item => {
              flatArray.push({
                ...item,
                day: day
              });
            });
          }
        });
        
        console.log('✅ GET - Weekly menu flattened:', flatArray);
        return { 
          status: true, 
          data: flatArray,
          grouped: groupedData
        };
      }
      
      if (Array.isArray(response.data)) {
        return { status: true, data: response.data };
      }
      
      if (Array.isArray(response)) {
        return { status: true, data: response };
      }
      
      return { status: true, data: [] };
    } catch (error) {
      console.error('❌ GET - Weekly menu error:', error);
      throw error;
    }
  };

  // ========== SUBSCRIPTION API (PRIVATE) ==========

  createSubscription = async (subscriptionData) => {
    try {
      console.log('🔄 POST - Creating monthly subscription:', subscriptionData);
      
      const items = (subscriptionData.items || []).map(item => ({
        meal_time: item.meal_time,
        meal_type: item.meal_type || 'Veg',
        quantity: item.quantity || 1
      }));
      
      const bookings = (subscriptionData.bookings || []).map(booking => ({
        date: booking.date,
        items: (booking.items || []).map(item => ({
          meal_time: item.meal_time,
          meal_type: item.meal_type || 'Veg',
          quantity: item.quantity || 1
        }))
      }));
      
      const payload = {
        start_date: subscriptionData.start_date,
        delivery_address: subscriptionData.delivery_address || 'Address not provided',
        items: items,
        bookings: bookings
      };
      
      console.log('📤 Sending subscription payload:', JSON.stringify(payload, null, 2));
      
      const response = await this.api.post('/tiffin/monthly/subscribe', payload);
      console.log('✅ POST - Subscription created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Create subscription error:', error);
      
      if (error.response?.data) {
        console.error('⚠️ Server error details:', error.response.data);
      }
      
      throw error;
    }
  };

  getSubscriptions = async () => {
    try {
      console.log('🔄 GET - Fetching subscriptions...');
      const response = await this.api.get('/tiffin/monthly');
      console.log('✅ GET - Subscriptions fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Subscriptions error:', error);
      throw error;
    }
  };

  getSubscriptionDetails = async (subscriptionId) => {
    try {
      console.log('🔄 GET - Fetching subscription details:', subscriptionId);
      const response = await this.api.get(`/tiffin/monthly/${subscriptionId}`);
      console.log('✅ GET - Subscription details:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Subscription details error:', error);
      throw error;
    }
  };

  cancelSubscription = async (subscriptionId) => {
    try {
      console.log('🔄 POST - Cancelling subscription:', subscriptionId);
      const response = await this.api.post(`/tiffin/monthly/${subscriptionId}/cancel`);
      console.log('✅ POST - Subscription cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Cancel subscription error:', error);
      throw error;
    }
  };

  // ========== CUSTOM DATE ORDER API (PRIVATE) ==========
  
  placeCustomDateOrder = async (orderData) => {
    try {
      console.log('🔄 POST - Placing custom date order:', orderData);
      
      const formattedData = {
        bookings: orderData.bookings.map(booking => ({
          date: booking.date,
          items: booking.items.map(item => ({
            meal_time: item.meal_time,
            meal_type: item.meal_type || 'Veg',
            quantity: item.quantity || 1
          }))
        }))
      };
      
      const response = await this.api.post('/tiffin/date-wise/order', formattedData);
      console.log('✅ POST - Custom date order placed:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Custom date order error:', error);
      throw error;
    }
  };

  // ✅ GET - Get Custom Date Order Details
  getCustomDateOrderDetails = async (orderId) => {
    try {
      console.log(`🔄 GET - Fetching custom date order details for ID: ${orderId}`);
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.get(`/tiffin/date-wise/${orderId}`);
      console.log('✅ GET - Custom order details fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Custom order details error:', error);
      throw error;
    }
  };

  // ✅ GET - Get All Custom Date Orders History
  getCustomDateOrdersHistory = async () => {
    try {
      console.log('🔄 GET - Fetching custom date orders history...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.get('/tiffin/date-wise/history');
      console.log('✅ GET - Custom orders history fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Custom orders history error:', error);
      throw error;
    }
  };

  cancelCustomDateOrder = async (orderId) => {
    try {
      console.log('🔄 POST - Cancelling custom date order:', orderId);
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.post(`/tiffin/date-wise/${orderId}/cancel`);
      console.log('✅ POST - Custom order cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Cancel custom order error:', error);
      
      if (error.response?.status === 404) {
        try {
          console.log('🔄 Trying alternative endpoint...');
          const response = await this.api.post(`/date-wise/${orderId}/cancel`);
          console.log('✅ POST - Custom order cancelled (alternative):', response.data);
          return response.data;
        } catch (altError) {
          console.error('❌ Alternative endpoint also failed:', altError);
          throw altError;
        }
      }
      throw error;
    }
  };

  // ========== USER API (PRIVATE) ==========
  
  getUserProfile = async () => {
    try {
      console.log('🔄 Fetching user profile...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      const response = await this.api.get('/user/profile');
      console.log('✅ Profile fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Get profile error:', error);
      throw error;
    }
  };

  updateUserProfile = async (userData) => {
    try {
      console.log('📤 Updating user profile...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      if (userData instanceof FormData) {
        const response = await this.api.post('/user/profile/update', userData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        console.log('✅ Profile updated with image:', response.data);
        return response.data;
      } else {
        const response = await this.api.post('/user/profile/update', userData);
        console.log('✅ Profile updated:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('❌ Update profile error:', error);
      throw error;
    }
  };

  deleteUserAccount = async () => {
    try {
      console.log('🗑️ Deleting user account...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.delete('/delete-account');
      console.log('✅ Account deleted:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Delete account error:', error);
      throw error;
    }
  };

  logoutUser = async () => {
    try {
      console.log('📤 Logging out user...');
      const token = localStorage.getItem('token');
      if (token && token !== 'undefined' && token !== 'null') {
        const response = await this.api.post('/logout');
        console.log('✅ Logged out successfully:', response.data);
        return response.data;
      }
      return { success: true };
    } catch (error) {
      console.error('❌ Logout error:', error);
      throw error;
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isNewUser');
      localStorage.removeItem('profileCompleted');
      localStorage.removeItem('tiffinPhone');
      localStorage.removeItem('tiffinUserLogin');
      console.log('🧹 LocalStorage cleared');
    }
  };

  // ========== PRODUCT API (PRIVATE - Requires Authentication) ==========
  
  getProducts = async () => {
    try {
      console.log('🔄 Fetching products...');
      const response = await this.api.get('/products');
      console.log('✅ Products fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Fetch products error:', error);
      throw error;
    }
  };

  // ========== CART API (PRIVATE) ==========
  
  addToCart = async (cartData) => {
    try {
      console.log('🔄 POST - Adding to cart:', cartData);
      const response = await this.api.post('/cart/add', cartData);
      console.log('✅ POST - Added to cart:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Add to cart error:', error);
      throw error;
    }
  };

  getCartItems = async () => {
    try {
      console.log('🔄 GET - Fetching cart items...');
      const response = await this.api.get('/cart');
      console.log('✅ GET - Cart items fetched:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ GET - Fetch cart error:', error);
      throw error;
    }
  };

  updateCart = async (updateData) => {
    try {
      console.log('🔄 POST - Updating cart:', updateData);
      const response = await this.api.post('/cart/update', updateData);
      console.log('✅ POST - Cart updated:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Update cart error:', error);
      throw error;
    }
  };

  removeFromCart = async (removeData) => {
    try {
      console.log('🔄 DELETE - Removing from cart:', removeData);
      const response = await this.api.delete('/cart/remove', {
        data: removeData
      });
      console.log('✅ DELETE - Removed from cart:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ DELETE - Remove from cart error:', error);
      throw error;
    }
  };

  // ✅ POST - Place Order - WITHOUT GST
  placeOrder = async (orderData) => {
    try {
      let cartItems = [];
      
      if (orderData.items && Array.isArray(orderData.items) && orderData.items.length > 0) {
        cartItems = orderData.items;
        console.log('📦 Got cart items from orderData:', cartItems);
      } else {
        try {
          const stored = localStorage.getItem('cartItems');
          if (stored) {
            cartItems = JSON.parse(stored);
            console.log('📦 Got cart items from localStorage:', cartItems);
          }
        } catch (e) {
          console.warn('Could not parse cart items from localStorage');
        }
      }
      
      if (!cartItems || cartItems.length === 0) {
        try {
          const reduxState = sessionStorage.getItem('persist:root');
          if (reduxState) {
            const parsed = JSON.parse(reduxState);
            if (parsed.cart) {
              const cartData = JSON.parse(parsed.cart);
              if (cartData.items && cartData.items.length > 0) {
                cartItems = cartData.items;
                console.log('📦 Got cart items from Redux state:', cartItems);
              }
            }
          }
        } catch (e) {
          console.warn('Could not get cart from Redux state');
        }
      }
      
      const formattedItems = cartItems.map(item => ({
        item_id: parseInt(item.item_id) || parseInt(item.id) || 0,
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        item_type: item.item_type || 'product'
      }));
      
      const subtotal = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const deliveryCharge = parseFloat(orderData.delivery_charge) || 0;
      const total = subtotal + deliveryCharge;
      
      const payload = {
        payment_method: orderData.payment_method || 'cash',
        customer_address: orderData.customer_address || orderData.address || '',
        customer_phone: orderData.customer_phone || orderData.phone || '',
        customer_name: orderData.customer_name || orderData.name || '',
        items: formattedItems,
        total: total,
        subtotal: subtotal,
        delivery_charge: deliveryCharge
      };
      
      if (orderData.latitude) payload.latitude = orderData.latitude;
      if (orderData.longitude) payload.longitude = orderData.longitude;
      if (orderData.user_id) payload.user_id = parseInt(orderData.user_id);
      
      console.log('🔄 POST - Complete order payload (NO GST):', JSON.stringify(payload, null, 2));
      console.log('📊 Order totals - Subtotal: ₹' + subtotal.toFixed(2) + ', Total: ₹' + total.toFixed(2));
      
      if (formattedItems.length === 0) {
        throw new Error('Your cart is empty. Please add items before placing an order.');
      }
      
      const response = await this.api.post('/checkout', payload);
      
      console.log('✅ POST - Order placed:', response.data);
      
      try {
        localStorage.removeItem('cartItems');
        sessionStorage.removeItem('persist:root');
      } catch (e) {
        console.warn('Could not clear cart:', e);
      }
      
      return response.data;
      
    } catch (error) {
      console.error('❌ POST - Place order error:', error);
      
      if (error.response) {
        console.error('⚠️ Error status:', error.response.status);
        console.error('⚠️ Error data:', error.response.data);
        
        if (error.response.status === 422) {
          const errorData = error.response.data;
          let errorMessage = 'Validation failed:\n';
          
          if (errorData.errors) {
            Object.keys(errorData.errors).forEach(field => {
              errorMessage += `- ${field}: ${errorData.errors[field].join(', ')}\n`;
            });
            throw new Error(errorMessage);
          } else if (errorData.message) {
            throw new Error(errorData.message);
          }
        }
      }
      
      throw error;
    }
  };

  // ✅ GET - Order History
  getOrderHistory = async () => {
    try {
      console.log('🔄 GET - Fetching order history...');
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.get('/orders/history');
      console.log('✅ GET - Order history response:', JSON.stringify(response.data, null, 2));
      
      return response.data;
    } catch (error) {
      console.error('❌ GET - Order history error:', error);
      throw error;
    }
  };

  // ✅ POST - Cancel Order
  cancelOrder = async (orderId) => {
    try {
      console.log('🔄 POST - Cancelling order:', orderId);
      const token = localStorage.getItem('token');
      if (!token || token === 'undefined' || token === 'null') {
        throw new Error('Please login first');
      }
      
      const response = await this.api.post(`/orders/${orderId}/cancel`);
      console.log('✅ POST - Order cancelled:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ POST - Cancel order error:', error);
      throw error;
    }
  };
}

const apiService = new ApiService();
export default apiService;